import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, Badge, linkTextClasses } from '@evogard/ui';
import { count, formatInstant, loadFailureMessage } from '@evogard/domain';
import { SENDABLE_CHANNELS } from '@evogard/contract';
import type {
  BoletoMember,
  BillingChannel,
  BoletoStatus,
  BoletoVehicle,
} from '@evogard/contract';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

/**
 * O vocabulário da cobrança na interna, num lugar só.
 *
 * As telas do módulo — a mesa, a régua, a esteira e o painel do início — dizem
 * as mesmas coisas sobre o mesmo mundo, e é aí que a repetição custa caro. Três
 * frases se repetem em todas elas e nenhuma pode divergir:
 *
 * 1. **"esta praça não tem régua"**, que é o estado das duas hoje. Escrito em
 *    cada arquivo, vira "nenhum boleto" numa tela e "nada configurado" na outra,
 *    e a pessoa que lê as duas conclui coisas diferentes sobre a mesma base;
 * 2. **o intervalo de dias de uma faixa**, que a mesa imprime no cabeçalho da
 *    coluna e a régua imprime na linha que se edita. Duas leituras do sinal de
 *    `minDays` é como "3 dias antes do vencimento" vira "-3 dias de atraso" numa
 *    das duas;
 * 3. **o estado do espelho**, que aparece em todo cartão da mesa.
 *
 * Nada aqui reimplementa regra. A etapa vem calculada do servidor
 * (`stageOf`, em `@evogard/domain`), o bloqueio de contato vem resolvido de
 * `common/contact-block.service.ts`, e a lista de canais que a casa realmente
 * envia é `SENDABLE_CHANNELS` do contrato — não uma segunda lista escrita aqui.
 */

/* ------------------------------------------------------------------------- *
 * A régua da base
 * ------------------------------------------------------------------------- */

/**
 * A régua da base atual — `GET /billing/tiers`.
 *
 * `enabled` existe porque a régua é de **outro recurso**: quem tem
 * `billing.desk:ver` e não tem `billing.tiers:ver` recebe 403 aqui. Buscar
 * assim mesmo faria a tela mostrar "não foi possível carregar" no lugar de
 * dizer qual permissão falta.
 *
 * `tenantId` na chave e `placeholderData` preso à mesma base: a régua da CE e a
 * da GO são conjuntos diferentes, e segurar a anterior durante a troca mostraria
 * as faixas de uma praça sob o nome da outra.
 */
export function useLadder({ enabled = true }: { enabled?: boolean } = {}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: ['cobranca', 'faixas', tenantId],
    enabled,
    queryFn: async () => {
      const r = await api.billing.tiers();
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a régua desta base'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });
}

/**
 * De onde a régua vem, escrito uma vez.
 *
 * `billing_tiers` nasce vazia nas duas praças: as faixas, os canais e as
 * janelas só existem no banco de produção da esteira legada, e a extração
 * (`cobranca-03`) não aconteceu. Semear uma régua por suposição é disparar
 * cobrança em cima de gente real (regra 14) — por isso nenhuma tela desta pasta
 * oferece um valor sugerido, e todas dizem de onde o valor de verdade sai.
 */
export const LADDER_SOURCE =
  'As faixas, os canais e as janelas de cada praça vêm do banco de produção da ' +
  'esteira legada de cobrança e são carregados de lá. A plataforma não inventa ' +
  'faixa: uma que nascesse aqui mandaria cobrança para gente real sob uma régua ' +
  'que ninguém decidiu.';

/**
 * A base sem régua — o estado de hoje nas duas praças.
 *
 * Não é falha de carregamento e não é "não há boleto": é a configuração que
 * ainda não existe. É o mesmo desenho de `QuadroNaoConfigurado` na retenção, e
 * pelo mesmo motivo — sem faixa não há etapa, e sem etapa não há coluna para
 * agrupar nada.
 */
export function NoLadderCard() {
  return (
    <Card title="Esta praça ainda não tem régua de cobrança.">
      <p className="text-sm">
        Não há nenhuma faixa em <strong className="font-medium">cobrança</strong> nesta
        base, então não há etapa calculada e não há coluna para agrupar os atendimentos —
        e um quadro vazio dizendo “nenhum boleto” diria sobre o mundo o que é ausência de
        configuração.
      </p>
      <p className="mt-2 text-sm text-texto-suave">{LADDER_SOURCE}</p>
      <p className="mt-2 text-sm text-texto-suave">
        Enquanto não houver faixa, a esteira não dispara nada: todo boleto responde “fora
        da régua”, que é a recusa mais definitiva que ela tem. A régua desta base se edita
        em{' '}
        <Link to="/billing/tiers" className={linkTextClasses()}>
          Régua de cobrança
        </Link>
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * A faixa: dias, canais e janela
 * ------------------------------------------------------------------------- */

/**
 * Um ponto da régua em português — e o sinal que o operador nunca precisa ler.
 *
 * `minDays` negativo é **antes do vencimento**: é o que permite o aviso
 * pré-vencimento na mesma tabela, sem uma segunda estrutura e sem um sinal
 * escondido em código. Quem cadastra a régua digita `-3`; quem lê a tela lê
 * "3 dias antes do vencimento". Imprimir "-3 dias de atraso" transformaria a
 * convenção da coluna em pergunta de interpretação, e a resposta errada é a
 * confortável: parece "3 dias de atraso" para quem lê rápido.
 */
function ladderDot(day: number): string {
  if (day < 0) return `${count(-day, 'dia', 'dias')} antes do vencimento`;
  if (day === 0) return 'o dia do vencimento';
  return `${count(day, 'dia', 'dias')} de atraso`;
}

/**
 * O intervalo de uma faixa, por extenso.
 *
 * O intervalo é **fechado nos dois lados** — `[diaMin, diaMax]` —, que é o que o
 * `int4range(dia_min, dia_max, '[]')` do `EXCLUDE` do banco avalia: um dia
 * exatamente no limite cai nesta faixa. Por isso o texto diz "de … a …" e nunca
 * "até", que em português é lido como exclusivo tantas vezes quanto inclusivo.
 *
 * `maxDays` nulo é a **faixa final, sem teto** — o `> 60` da esteira legada. Não
 * se inventa um número aqui: um `9999` apareceria na tela como se alguém tivesse
 * decidido aquele limite.
 *
 * Este ramo já era alcançado pela tela da régua e passou a ser alcançado também
 * pela MESA: `colunaDaMesaSchema.diaMax` virou `.nullable()` e `groupDesk`
 * deixou de derrubar a requisição na faixa final. Antes disso a mesa devolvia 500 para toda
 * base com régua carregada — e a tela estava certa o tempo todo.
 */
export function dayRangeText(minDay: number, maxDay: number | null): string {
  if (maxDay === null) return `A partir de ${ladderDot(minDay)}`;
  if (minDay === maxDay) {
    const dot = ladderDot(minDay);
    return dot.charAt(0).toUpperCase() + dot.slice(1);
  }
  // os dois do mesmo lado do vencimento compactam sem perder o lado
  if (minDay < 0 && maxDay < 0) {
    return `De ${-minDay} a ${count(-maxDay, 'dia', 'dias')} antes do vencimento`;
  }
  if (minDay > 0 && maxDay > 0) {
    return `De ${minDay} a ${count(maxDay, 'dia', 'dias')} de atraso`;
  }
  return `De ${ladderDot(minDay)} a ${ladderDot(maxDay)}`;
}

/**
 * `HH:MM:SS` → `HH:MM`.
 *
 * O Postgres devolve `time` com os segundos e o contrato aceita as duas formas
 * (`localTimeSchema`). Segundo numa janela de envio é ruído: ninguém declara
 * "das 08:00:00 às 18:00:00".
 */
export function trimSeconds(hour: string): string {
  return hour.slice(0, 5);
}

/**
 * A limitação de fuso, dita onde a janela aparece.
 *
 * A janela é **hora local da praça** — a coluna é `time` sem fuso porque "não
 * mande mensagem antes das 8h" é uma regra sobre o relógio de quem recebe. Só
 * que **o fuso da praça não existe na casa**: não há coluna em `app.tenants` nem
 * chave em `CONFIGURATION_CATALOG`, e `withinRangeWindow`
 * (`billing.pipeline.service.ts`) avalia com `HOUSE_TIMEZONE`
 * (`America/Fortaleza`), explicitamente.
 *
 * Hoje as duas praças estão em UTC-3 sem horário de verão e as duas leituras
 * coincidem — mas isso é coincidência de calendário, não regra. Uma tela que
 * escrevesse "no fuso da praça" estaria afirmando o que o código não cumpre; por
 * isso ela escreve o fuso que de fato é usado, e diz que é uma limitação.
 */
export const TIMEZONE_NOTICE =
  'A janela é hora local da praça — mas o fuso de cada base ainda não existe na ' +
  'plataforma, e hoje o horário é avaliado no fuso da casa (America/Fortaleza). ' +
  'As duas praças estão no mesmo horário, então as leituras coincidem; no dia em ' +
  'que uma delas mudar, esta janela desloca sem ninguém ter mexido nela.';

export const CHANNEL_LABEL: Record<BillingChannel, string> = {
  email: 'E-mail',
  whatsapp: 'WhatsApp',
};

/**
 * Este canal sai da casa hoje?
 *
 * `common/notifications.service.ts` manda e-mail, e só. WhatsApp precisa poder ser
 * **gravado** numa faixa — senão a extração de `cobranca-03` não tem onde ser
 * registrada e a régua nova nasce diferente da que está no ar —, mas gravar não
 * é enviar. Quem sabe a diferença é `SENDABLE_CHANNELS`, no contrato; uma segunda
 * lista escrita aqui divergiria no dia em que o WhatsApp entrasse de verdade, e
 * o sintoma seria a tela prometendo um disparo que ninguém faz.
 */
export function channelDispatches(channel: BillingChannel): boolean {
  return (SENDABLE_CHANNELS as readonly BillingChannel[]).includes(channel);
}

/** Os canais que a faixa declara e ainda não saem daqui. Vazio é o caso feliz. */
export function channelsNotDispatching(
  channels: readonly BillingChannel[],
): BillingChannel[] {
  return channels.filter((c) => !channelDispatches(c));
}

/* ------------------------------------------------------------------------- *
 * Dinheiro
 * ------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------- *
 * O espelho, e os três estados que a tela precisa distinguir
 * ------------------------------------------------------------------------- */

/**
 * `sem_espelho` é problema NOSSO, e o texto diz isso.
 *
 * É o estado de todo cartão hoje: o espelho do ERP está construído e vazio nas
 * duas praças até a carga inicial contra o SGA de produção (`erp-01`/`erp-02`).
 * Escrever "associado não encontrado" culparia o cadastro da pessoa por uma
 * sincronização que a casa ainda não rodou — e mandaria quem cobra procurar no
 * ERP um dado que está lá.
 */
const WITHOUT_MIRROR =
  'Esta praça ainda não sincronizou o espelho do ERP — por isso o nome e o ' +
  'contato não aparecem. Não quer dizer que o cadastro não exista.';

const NOT_ENCONTRADO_MEMBER =
  'O espelho desta praça respondeu e não tem este código de associado.';

const NOT_ENCONTRADO_VEHICLE =
  'O espelho desta praça respondeu e não tem este código de veículo.';

/** O código é NOSSO — está em `boletos` — e continua legível nos três estados. */
function Code({ label, code }: { label: string; code: string }) {
  return (
    <span className="tabular-nums">
      <span className="text-texto-suave">{label} </span>
      {code}
    </span>
  );
}

/**
 * O associado do boleto.
 *
 * `detalhado` acrescenta o contato — telefone e e-mail, que é do que quem cobra
 * precisa para ligar e para escrever. No cartão da coluna ele não entra: um
 * telefone por cartão numa fila de trezentos é ruído, e o contato é a informação
 * que a pessoa lê no instante em que vai usar.
 */
export function BoletoMemberView({
  member,
  detailed = false,
}: {
  member: BoletoMember;
  detailed?: boolean;
}) {
  if (member.estado !== 'encontrado') {
    return (
      <div className="min-w-0">
        <Code label="Associado" code={member.code} />
        <p className="mt-0.5 text-xs text-texto-suave">
          {member.estado === 'sem_espelho' ? WITHOUT_MIRROR : NOT_ENCONTRADO_MEMBER}
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <p className="truncate font-medium">
        {/* nulo dentro de `encontrado` é "o espelho não tem esse campo", que não
            é nenhum dos dois estados de cima — e a frase muda por isso */}
        {member.nome ?? (
          <span className="font-normal text-texto-suave">
            O espelho tem este associado e não tem o nome dele.
          </span>
        )}
      </p>
      <p className="mt-0.5 truncate text-xs text-texto-suave">
        <Code label="Associado" code={member.code} />
        {member.maskedCpf && <> · CPF {member.maskedCpf}</>}
      </p>
      {detailed && (
        <dl className="mt-2 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Contact label="Telefone" contact={member.telefone} />
          <Contact label="E-mail" contact={member.email} />
          <div className="sm:col-span-2">
            <dt className="rotulo text-texto-suave">Espelho lido em</dt>
            <dd className="mt-0.5 text-xs text-texto-suave">
              {formatInstant(member.syncedAt)} — é o que o ERP dizia naquele instante, não
              o estado dele agora.
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

/**
 * Um contato, com o bloqueio já resolvido pelo servidor.
 *
 * `bloqueado` vem de `common/contact-block.service.ts`, respondido **uma vez,
 * no servidor**. A tela não decide isso: se ela recebesse só o valor e
 * consultasse por conta própria, teríamos a segunda implementação da mesma regra
 * — e é justamente a regra que o Kanban de hoje não tem, e que faz a casa ligar
 * para quem pediu para não ser contatada.
 */
function Contact({
  label,
  contact,
}: {
  label: string;
  contact: { value: string | null; blocked: boolean };
}) {
  return (
    <div>
      <dt className="rotulo text-texto-suave">{label}</dt>
      <dd className="mt-0.5 flex flex-wrap items-center gap-2">
        {contact.value ?? (
          <span className="text-texto-suave">O espelho não trouxe este campo.</span>
        )}
        {contact.blocked && <Badge tone="alerta">Não contatar</Badge>}
      </dd>
    </div>
  );
}

/**
 * Este cartão tem algum contato bloqueado?
 *
 * A pergunta é do **cartão**, não do canal: quem vai ligar precisa saber antes
 * de discar, e o selo da coluna é a única chance de dizer isso sem clicar. O
 * bloqueio vale para toda base e todo canal (regra 15) — a tabela
 * `app.contact_blocks` não tem `tenant_id`, e a ausência é a decisão.
 */
export function hasBlockedContact(member: BoletoMember): boolean {
  if (member.estado !== 'encontrado') return false;
  return member.telefone.blocked || member.email.blocked;
}

export function BoletoVehicleView({ vehicle }: { vehicle: BoletoVehicle }) {
  if (vehicle.estado !== 'encontrado') {
    return (
      <div className="min-w-0">
        <Code label="Veículo" code={vehicle.code} />
        <p className="mt-0.5 text-xs text-texto-suave">
          {vehicle.estado === 'sem_espelho' ? WITHOUT_MIRROR : NOT_ENCONTRADO_VEHICLE}
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <p className="truncate font-medium tabular-nums">
        {/*
         * Placa nula dentro de `encontrado` é um FATO DO CADASTRO, e não um
         * problema nosso: veículo sem placa existe no ERP (agregado, implemento,
         * cadastro em andamento) e o espelho tem chave única parcial por causa
         * disso. É por isso que a frase é diferente das duas de cima.
         */}
        {vehicle.placa ?? (
          <span className="font-normal text-texto-suave">
            Veículo sem placa no cadastro
          </span>
        )}
      </p>
      <p className="mt-0.5 truncate text-xs text-texto-suave">
        <Code label="Veículo" code={vehicle.code} />
        {vehicle.modelo && <> · {vehicle.modelo}</>}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * A situação do boleto
 * ------------------------------------------------------------------------- */

/**
 * A situação, e por que ela não vira selo em todo cartão.
 *
 * `app.erp_status_classifications` nasce **vazia**: enquanto for assim, toda
 * situação responde `nao_classificada` — e esse é o único caminho que roda hoje,
 * não a borda rara. Um selo neutro em todos os trezentos cartões dizendo "não
 * classificada" gastaria a atenção que o selo existe para comprar, e faria a
 * coluna inteira parecer pintada. Então:
 *
 * - `nao_classificada` é **texto**, na lista de campos do cartão, com a
 *   descrição do fornecedor ao lado quando ela existe;
 * - `classificada` vira selo só quando há um fato a anunciar — pago ou
 *   cancelado. Classificada e em aberto é o esperado, e o esperado não leva selo.
 *
 * **Nenhuma tela pode ler `nao_classificada` como "em aberto".** Ler é o que os
 * quatro sistemas antigos fazem comparando descrição, e é o que faz um boleto
 * pago voltar a ser cobrado — `BAIXADO EM CARTORIO` estava pago para dois deles
 * e não para os outros dois.
 */
export function StatusBadge({ situacao }: { situacao: BoletoStatus }) {
  if (situacao.estado !== 'classificada') return null;
  if (situacao.pago) return <Badge tone="sucesso">Pago</Badge>;
  if (situacao.canceled) return <Badge tone="neutro">Cancelado</Badge>;
  return null;
}

/** A situação por extenso — para a linha de campos, onde cabe a explicação. */
export function BoletoStatusText({ situacao }: { situacao: BoletoStatus }) {
  const code = (
    <span className="tabular-nums text-texto-suave">código {situacao.code || '—'}</span>
  );

  if (situacao.estado === 'nao_classificada') {
    return (
      <span>
        {situacao.descricao ?? 'Sem descrição no espelho'} — a casa ainda não classificou
        este código, então não dá para dizer se está pago. {code}
      </span>
    );
  }

  return (
    <span>
      {situacao.descricao ?? 'Sem descrição no espelho'}
      {situacao.pago && ' · pago'}
      {situacao.canceled && ' · cancelado'} {code}
    </span>
  );
}

/* ------------------------------------------------------------------------- *
 * Quando foi
 * ------------------------------------------------------------------------- */

/*
 * Reexportado, e não redeclarado: os dois moram em `components/Values.tsx`
 * porque dois módulos precisavam deles e a saída fácil tinha sido copiar.
 */
export { Age, MoneyValue } from '../../components/Values.js';
